import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Circle, Building2, Clock, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

const STATUS_LABEL = {
  draft: "Awaiting send",
  sent: "Awaiting your signature",
  signed: "Signed — kicking off",
  active: "In delivery",
  completed: "Completed",
};

export default function PublicMilestones() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    axios.get(`${API}/public/contract-status/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Link not found"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center p-8">
        <div className="max-w-md text-center border border-neutral-800 p-10 bg-[#141B2D]">
          <h1 className="font-display text-2xl font-bold mb-2">Status link not found</h1>
          <p className="text-neutral-400 text-sm">This link is invalid or expired. Contact ClientsOnRamp for a new one.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0A0E1A] text-white flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  const c = data.contract;
  const p = data.progress;

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white p-6" data-testid="public-milestones-page">
      <div className="max-w-3xl mx-auto py-10">
        <div className="flex items-center gap-2 mb-8">
          <Building2 className="w-5 h-5 text-[#FF6B47]" />
          <span className="font-display text-xl font-bold">ClientsOnRamp</span>
        </div>

        <div className="text-xs font-mono uppercase tracking-widest text-[#F5B840] mb-2 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Delivery status
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold mb-2" style={{ letterSpacing: "-0.03em" }}>
          {c.package_name}
        </h1>
        <div className="text-neutral-400 text-sm mb-8">
          {data.client?.business_name && <>For <strong className="text-white">{data.client.business_name}</strong> · </>}
          Contract № <span className="font-mono">{c.id.slice(0, 8).toUpperCase()}</span>
        </div>

        {/* Progress hero */}
        <div className="border border-[#FF6B47]/40 bg-[#141B2D] p-8 mb-8">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Progress</div>
              <div className="text-5xl font-bold text-[#FF6B47] mt-1">{p.percent}%</div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Milestones</div>
              <div className="text-2xl font-bold mt-1">{p.done}/{p.total || 0}</div>
            </div>
          </div>
          <div className="h-3 bg-neutral-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#FF6B47] to-[#F5B840] transition-all" style={{ width: `${p.percent}%` }} data-testid="progress-bar-fill" />
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 text-sm">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Status</div>
              <div className="font-semibold mt-1">{STATUS_LABEL[c.status] || c.status}</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Deliver by</div>
              <div className="font-semibold mt-1">{fmt(c.delivery_by)}</div>
            </div>
            {data.job?.job_number && (
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Job</div>
                <div className="font-semibold mt-1 font-mono">{data.job.job_number}</div>
              </div>
            )}
          </div>
        </div>

        {/* Milestones list */}
        {data.milestones.length > 0 ? (
          <div className="space-y-3">
            {data.milestones.map((m, i) => {
              const done = !!m.completed_at;
              return (
                <div
                  key={m.id}
                  data-testid={`public-milestone-${i}`}
                  className={`border p-5 flex items-start gap-4 ${done ? "border-emerald-500/40 bg-emerald-900/20" : "border-neutral-800 bg-[#141B2D]"}`}
                >
                  {done ? <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" /> : <Circle className="w-6 h-6 text-neutral-600 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className={`font-display text-lg font-bold ${done ? "text-emerald-200" : ""}`}>
                      {m.title}
                    </div>
                    {m.description && <div className="text-sm text-neutral-400 mt-1">{m.description}</div>}
                    <div className="text-xs text-neutral-500 mt-2 font-mono flex flex-wrap gap-x-4">
                      {m.target_date && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> target {fmt(m.target_date)}</span>}
                      {m.completed_at && <span className="text-emerald-400">completed {fmt(m.completed_at)}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border border-dashed border-neutral-700 bg-[#141B2D] p-12 text-center text-neutral-400">
            <div className="font-display text-xl mb-1">No milestones yet</div>
            <p className="text-sm">Your delivery team will populate this timeline shortly. Check back soon.</p>
          </div>
        )}

        <div className="text-center text-xs text-neutral-500 mt-12 font-mono">
          Questions? Reply to any email from ClientsOnRamp — we'll get back within one business day.
        </div>
      </div>
    </div>
  );
}
